import { useState, useEffect, useRef } from 'react';
import { guard, objectify } from 'radash';
import { fetchInbox } from 'datamodel/Inbox/api';
import { getAPIUrl } from 'base/shared/Utils';
import envVars from 'base/shared/Env';
import fetch from 'base/shared/fetch-data';
import { CHATS_PER_PAGE } from './constants';
import { CURRENT_USER_ROLES } from 'app/datamodel/Inbox/constants';
import { User } from 'datamodel/User/models';

const getFirstRequest = (requests) => ({
  currentRequest: requests[0],
  idOfPreviousRequest: null,
  idOfNextRequest: requests[1]?.id,
});

const findRequestWithId = (requests, id) => {
  if (!id) {
    return;
  }
  const currentRequestIndex = requests.findIndex((request) => request.id == id);
  const currentRequest = requests[currentRequestIndex];
  if (!currentRequest) {
    return;
  }
  return {
    currentRequest,
    idOfPreviousRequest: requests[currentRequestIndex - 1]?.id,
    idOfNextRequest: requests[currentRequestIndex + 1]?.id,
  };
};

const getConnectionEnquiry = async (id, token) => {
  const { BADI_CONNECTION_ENQUIRY } = envVars();
  const serverUrl = getAPIUrl(BADI_CONNECTION_ENQUIRY.replace(':id', id));

  const response = await fetch(serverUrl, {
    token,
  });
  return response.data;
};

const fetchUser = (id, token) => {
  const { BADI_USER_SERVICE } = envVars();
  const serverUrl = getAPIUrl(BADI_USER_SERVICE.replace('{id}', id));

  return fetch(serverUrl, { token }).then((res) => new User(res).toJSON());
};

const fetchConnectionsAndEnquiries = async ({ token, room, page }) => {
  const result = await fetchInbox(
    { token },
    {
      page,
      per: 5,
      room,
      current_user_role: CURRENT_USER_ROLES.LISTER,
      category: 'enquiry_pending',
      sorting: 'chronological',
    }
  );
  const { data, pagination } = result;
  const connections = data.connections;
  const requests = await Promise.all(
    connections.map(async (connection) => {
      const enquiry = await guard(() => getConnectionEnquiry(connection.id, token));
      const userdata = await guard(() => fetchUser(enquiry.user.id, token));
      enquiry.user = { ...userdata, ...enquiry.user };
      return { id: connection.id, connection, enquiry };
    })
  );
  return {
    data: requests.filter((request) => Boolean(request.enquiry)),
    pagination,
  };
};

const countRequestsToTheRightOf = (currentRequest, requests) => {
  const indexOfCurrentRequest = requests.findIndex((request) => request.id == currentRequest.id);
  return requests.length - 1 - indexOfCurrentRequest;
};

const areThereRequestsRemainingToFetch = ({ current_page, total_pages }) =>
  current_page < total_pages;

// this controls how eagerly we fetch further requests
// i.e if there are fewer than x remaining requests to display, we load more.
const EAGER_LOADING_OFFSET = 3;

const DEFAULT_PAGINATION = {
  total: 0,
  current_page: 1,
  total_pages: 1,
};

const setRequestFromCache = (id, token, room, resetRequests, data, setData) => {
  const requestFromCache = findRequestWithId(data.requests, id);
  if (!resetRequests && requestFromCache?.currentRequest) {
    setData({ ...data, ...requestFromCache, isLoaded: true });
    if (
      countRequestsToTheRightOf(requestFromCache.currentRequest, data.requests) ==
        EAGER_LOADING_OFFSET &&
      areThereRequestsRemainingToFetch(data.pagination)
    ) {
      fetchConnectionsAndEnquiries({
        token,
        room,
        page: data.pagination.current_page + 1,
      }).then((result) => {
        const requestsById = objectify(data.requests, ({ id }) => id);
        const newRequests = result.data.filter(({ id }) => !requestsById[id]);
        const requests = [...data.requests, ...newRequests];
        const request = findRequestWithId(requests, id) || getFirstRequest(requests);
        setData({
          ...data,
          ...request,
          pagination: result.pagination,
          requests,
          room,
          isLoaded: true,
        });
      });
    }
  } else {
    setData({ ...data, isLoaded: false });
    fetchConnectionsAndEnquiries({ token, room }).then((result) => {
      const requests = resetRequests ? [...result.data] : [...data.requests, ...result.data];
      const request = findRequestWithId(requests, id) || getFirstRequest(requests);
      setData({
        ...data,
        ...request,
        pagination: result.pagination,
        requests,
        room,
        isLoaded: true,
      });
    });
  }
};

const fetchDataForRequests = (token, id, room) => {
  const prevRoomRef = useRef();
  const [data, setData] = useState({
    pagination: DEFAULT_PAGINATION,
    requests: [],
    currentRequest: null,
    idOfPreviousRequest: null,
    idOfNextRequest: null,
    isLoaded: false,
    room: null,
  });
  useEffect(() => {
    const resetRequests = prevRoomRef.current !== undefined && prevRoomRef.current !== room;
    setRequestFromCache(id, token, room, resetRequests, data, setData);
    prevRoomRef.current = room;
  }, [id, room]);

  return data;
};

const fetchDataForChats = (token, filters, { page }, dependencies = []) => {
  const { roomId, category } = filters;
  const [data, setData] = useState({
    pagination: DEFAULT_PAGINATION,
  });
  useEffect(() => {
    fetchInbox(
      { token },
      {
        page: page || 1,
        per: CHATS_PER_PAGE,
        current_user_role: CURRENT_USER_ROLES.LISTER,
        room: roomId,
        category,
      }
    ).then(({ data: { connections }, pagination }) => {
      setData({ data: connections, pagination, isLoaded: true });
    });
  }, [roomId, category, page, ...dependencies]);
  return data;
};

export { fetchDataForRequests, fetchDataForChats, findRequestWithId };
